import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import config from "../Config";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useNavigate } from "react-router-dom";
import { useSingleFlight } from "../hooks/useSingleFlight";
import './pages.css';

const BRAND_ICONS = {
  visa: '💳 VISA',
  mastercard: '💳 Mastercard',
  amex: '💳 Amex',
  discover: '💳 Discover',
};

const brandLabel = (brand) =>
  BRAND_ICONS[brand?.toLowerCase()] ?? `💳 ${brand?.toUpperCase() ?? 'Card'}`;

const PaymentMethods = ({ amount }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const runPay = useSingleFlight();

  useEffect(() => {
    let cancelled = false;
    const fetchPaymentMethods = async () => {
      try {
        const token = Cookies.get("runner_app");
        const response = await authFetch(`${config.AUTH_API}/v1/get-payment-methods`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        await HandleResponse(response, navigate);
        const data = await response.json();
        if (!cancelled) setPaymentMethods(data.paymentMethods || []);
      } catch (err) {
        if (!cancelled) console.error("Error fetching payment methods:", err);
      }
    };
    fetchPaymentMethods();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    if (!selectedMethod) { setMessage("Please select a payment method."); return; }

    runPay(async () => {
      setLoading(true);
      try {
        const token = Cookies.get("runner_app");
        const response = await authFetch(
          `${config.AUTH_API}/v1/charge-stored-method?paymentMethodId=${selectedMethod}&amount=${amount}&currency=usd`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        if (response.ok) {
          setSuccess(true);
          setMessage("Payment successful! Credits have been added to your account.");
        } else {
          setSuccess(false);
          setMessage(data.error || "Payment failed. Please try again.");
        }
        setShowPopup(true);
      } catch (err) {
        console.error("Error processing payment:", err);
        setSuccess(false);
        setMessage("Payment failed. Please try again.");
        setShowPopup(true);
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        {paymentMethods.length > 0 ? (
          <div className="bill-methods-list">
            {paymentMethods.map((method) => (
              <label
                key={method.id}
                className={`bill-method-row ${selectedMethod === method.id ? 'bill-method-row--selected' : ''}`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value={method.id}
                  checked={selectedMethod === method.id}
                  onChange={() => setSelectedMethod(method.id)}
                  className="bill-method-radio"
                />
                <span className="bill-method-brand">{brandLabel(method.brand)}</span>
                <span className="bill-method-detail">
                  •••• {method.last4}
                </span>
                <span className="bill-method-exp">
                  Exp {method.exp_month}/{method.exp_year}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="bill-no-methods">No saved payment methods. Add a new card below.</p>
        )}

        {paymentMethods.length > 0 && (
          <button
            type="submit"
            className="bill-submit-btn"
            disabled={loading || !selectedMethod}
            style={{ marginTop: '1rem' }}
          >
            {loading ? 'Processing…' : `Pay $${(amount / 100).toFixed(2)}`}
          </button>
        )}
      </form>

      {/* Result modal */}
      {showPopup && (
        <div className="bill-modal-overlay">
          <div className="bill-modal">
            <div className={`bill-modal-icon${success ? ' bill-modal-icon--success' : ''}`}>
              {success ? '✓' : '⚠'}
            </div>
            <h3 className="bill-modal-title">{success ? 'Payment Successful' : 'Payment Failed'}</h3>
            <p className="bill-modal-body">{message}</p>
            <button
              className="bill-modal-btn"
              onClick={() => { setShowPopup(false); if (success) window.location.reload(); }}
            >
              {success ? 'Done' : 'Try Again'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentMethods;
